// C:\VUE3\platform\src\store\notifications\useAlertsStore.js
import { defineStore } from "pinia";
import { ref, computed } from "vue";
import axios from "axios"; // ※ 프로젝트에서 커스텀 axios 인스턴스를 쓰면 여기만 교체
import { useLocalStorage } from "@vueuse/core";
import userUtils from "@/common/userUtils";
import notificationApi from "@/common/api/mypage/notificationApi";

export const useAlertsStore = defineStore("alerts", () => {
  const cursorKey = "alerts:lastId";
  const etagKey = "alerts:etag";

  const lastId = ref(localStorage.getItem(cursorKey) || "0");
  const etag = ref(localStorage.getItem(etagKey) || "");
  const running = ref(false);
  const timer = ref(null); // setTimeout id 보관

  // --- 단일 요청 가드 & 백오프 상태 ---
  let inFlight = false;
  let failCount = 0;
  const BASE_BACKOFF = 5000; // 5s
  const MAX_BACKOFF = 60000; // 60s

  const inbox = ref([]); // {id, text, type, ts, read}
  const maxInbox = 50;

  // ─────────────────────────────────────────────────────────────
  // 서버 DB 영속 알림(새로고침에도 유지되는 뱃지용)
  //  - serverAlarms: 서버에서 로드한 알림의 alertId 목록
  //  - readIds: 읽음 처리된 alertId 목록 (MyAlarm과 동일한 localStorage 키 공유)
  // ─────────────────────────────────────────────────────────────
  const serverAlarms = ref([]); // [{ alertId }]
  const readIds = useLocalStorage("readAlarms", []); // MyAlarm.vue 와 동일 키

  function addReadId(id) {
    if (id == null) return;
    if (!readIds.value.includes(id)) readIds.value.push(id);
  }

  // 서버 영속 알림 중 안 읽은 수
  const serverUnreadCount = computed(() =>
    serverAlarms.value.reduce(
      (n, a) => n + (readIds.value.includes(a.alertId) ? 0 : 1),
      0
    )
  );

  // 헤더 뱃지 = 라이브(메모리 inbox) 안읽음 + 서버 영속 안읽음
  const unreadCount = computed(
    () => inbox.value.filter((m) => !m.read).length + serverUnreadCount.value
  );

  /** 서버 DB에서 영속 알림을 로드해 뱃지에 반영 (새로고침/재접속 시 유지) */
  async function loadServerUnread() {
    const suid = readUserSuid();
    if (!suid) return;
    try {
      const res = await notificationApi.selectNotifications({
        userSuid: suid,
        pagingParam: { pageNum: 1, pageSize: 100, blockSize: 5 },
      });
      const list = res?.notificationLogList;
      serverAlarms.value = Array.isArray(list)
        ? list
            .filter((d) => d && d.alertId != null)
            .map((d) => ({ alertId: d.alertId }))
        : [];
      dbg("loadServerUnread", { count: serverAlarms.value.length });
    } catch (e) {
      dbg("loadServerUnread error", e?.message);
    }
  }

  // 디버그 토글: localStorage.setItem('alerts:debug','1') 로 켜기
  const debugEnabled = () =>
    typeof localStorage !== "undefined" && localStorage.getItem("alerts:debug") === "1";
  const dbg = (...args) => {
    if (debugEnabled()) console.log("[alertsStore]", ...args);
  };

  // ─────────────────────────────────────────────────────────────
  // UI용 단일 알림(토스트/팝업) 상태 (기존 useAlertStore 통합)
  // ─────────────────────────────────────────────────────────────
  const message = ref("");
  const type = ref("success");
  const visible = ref(false);

  function showAlert(msg, alertType = "success") {
    message.value = msg;
    type.value = alertType;
    visible.value = true;
  }

  function closeAlert() {
    visible.value = false;
  }

  // ===== accepted 래퍼 대응 유틸 =====
  function unwrapEnvelope(data) {
    if (!data)
      return { items: [], truncated: false, nextAfter: null, rejected: false };
    if (typeof data.accepted === "boolean") {
      if (data.accepted !== true)
        return { items: [], truncated: false, nextAfter: null, rejected: true };
      data = data.content || {};
    }
    const items = Array.isArray(data.items) ? data.items : [];
    return {
      items,
      truncated: !!data.truncated,
      nextAfter: data.nextAfter ?? null,
      rejected: false,
    };
  }
  function unwrapItem(m) {
    if (m && typeof m.accepted === "boolean") {
      if (m.accepted !== true) return null;
      return m.content || null;
    }
    return m;
  }
  function composeText(m) {
    if (m?.text) return String(m.text);
    const title = m?.title ? String(m.title) : "";
    const body = m?.body ? String(m.body) : "";
    if (title && body) return `${title}\n${body}`;
    return title || body || "";
  }
  function pushItems(items) {
    items.forEach((_m) => {
      const m = unwrapItem(_m);
      if (!m) return;
      const id = m.id != null ? m.id : m.alertId != null ? m.alertId : null;
      if (id == null) return;
      inbox.value.unshift({
        id,
        text: composeText(m),
        type: m.type || "info",
        ts: m.ts || m.createdAt || Date.now(),
        read: false,
      });
    });
    if (inbox.value.length > maxInbox) inbox.value.length = maxInbox;
  }

  function pushEstimateAlert({ title, body, adminEstimateId }) {
    const id = `estimate-${adminEstimateId}-${Date.now()}`;
    inbox.value.unshift({
      id,
      text: body ? `${title}\n${body}` : title,
      type: "estimate",
      ts: new Date().toISOString(),
      read: false,
      adminEstimateId,
      // 장바구니로 이동할 수 있도록 링크 정보 포함
      link: "/cartview",
    });
    if (inbox.value.length > maxInbox) inbox.value.length = maxInbox;
  }

  /** 일반 알림(SSE 등) 수신 시 inbox에 적재 */
  function pushInboxMessage({ title, body, type: t = "info", link = null }) {
    const id = `msg-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const text = body ? `${title}\n${body}` : title;
    inbox.value.unshift({
      id,
      text,
      type: t,
      ts: new Date().toISOString(),
      read: false,
      link,
    });
    if (inbox.value.length > maxInbox) inbox.value.length = maxInbox;
  }

  function markAllRead() {
    inbox.value.forEach((m) => (m.read = true));
    // 서버 영속 알림도 모두 읽음 처리 → 새로고침 후에도 뱃지 0 유지
    serverAlarms.value.forEach((a) => addReadId(a.alertId));
  }
  function markRead(id) {
    const it = inbox.value.find((m) => m.id === id);
    if (it) it.read = true;
    // 서버 영속 알림(숫자 alertId)인 경우 읽음 상태를 localStorage에 영속화
    if (id != null && !String(id).startsWith("msg-")) {
      addReadId(id);
    }
  }
  function clearInbox() {
    inbox.value = [];
  }

  function removeInboxItem(id) {
    const idx = inbox.value.findIndex((m) => m.id === id);
    if (idx >= 0) inbox.value.splice(idx, 1);
  }

  // ✅ 로그인 사용자 SUID 읽기 (없으면 null)
  function readUserSuid() {
    if (!userUtils || typeof userUtils.getUserInfo !== "function") return null;
    const info = userUtils.getUserInfo();
    const s = (info?.userSuid == null ? "" : String(info.userSuid)).trim();
    return s.length ? s : null;
  }

  // ETag → lastId
  function idFromEtag(et) {
    if (!et) return null;
    const clean = et.replace(/^W\//, "").replace(/"/g, "").trim();
    const parts = clean.split("-");
    const num = parts.length === 2 ? Number(parts[1]) : Number(clean);
    return Number.isFinite(num) ? String(num) : null;
  }
  // ETag에서 부팅 epoch 추출
  function bootFromEtag(et) {
    if (!et) return null;
    const clean = et.replace(/^W\//, "").replace(/"/g, "").trim();
    const parts = clean.split("-");
    const boot = parts[0];
    const n = Number(boot);
    return Number.isFinite(n) ? String(n) : null;
  }

  // ===== 백오프/스케줄 유틸 =====
  function calcBackoffMs() {
    const exp = Math.min(failCount, 6); // 5s→10s→20s→40s→60s 고정
    const base = BASE_BACKOFF * Math.pow(2, exp);
    const jitter = Math.floor(Math.random() * 1000);
    return Math.min(base + jitter, MAX_BACKOFF);
  }
  function schedule(ms) {
    clearTimeout(timer.value);
    timer.value = setTimeout(tick, ms);
  }

  /**
   * SSE 등 외부 트리거로 "지금 바로" 서버 알림을 다시 당겨오고 싶을 때 사용
   * - 이미 polling 중이면 즉시 1회 tick 스케줄
   * - polling 중이 아니면 startPolling()으로 시작
   */
  function refreshNow() {
    if (!readUserSuid()) return
    if (!running.value) {
      startPolling()
      return
    }
    schedule(0)
  }
  function parseRetryAfterMs(resp) {
    const raw = resp?.headers?.["retry-after"];
    if (!raw) return null;
    const n = Number(raw);
    if (Number.isFinite(n)) return Math.max(0, n * 1000);
    // HTTP-date 형식
    const dt = Date.parse(raw);
    if (Number.isFinite(dt)) return Math.max(0, dt - Date.now());
    return null;
  }

  async function tick(epochRetry = false) {
    // 중복 호출 가드 + 실행 중단 상태 체크
    if (!running.value || inFlight) return;
    const suid = readUserSuid();
    if (!suid) {
      dbg("tick skip: no userSuid", userUtils?.getDebugInfo?.());
      return;
    }

    // 오프라인이면 잠시 중단 후 온라인에서 재개
    if (navigator?.onLine === false) {
      stopPolling();
      window.addEventListener("online", () => startPolling(), { once: true });
      return;
    }

    inFlight = true;
    try {
      dbg("tick request", { suid, after: lastId.value, etag: etag.value });
      const res = await axios.get("/api/public/notification/alerts/next", {
        params: {
          userSuid: suid,
          after: lastId.value,
          limit: 50,
          drain: true,
          waitMs: 25000,
        },
        headers: etag.value ? { "If-None-Match": `"${etag.value}"` } : {},
        timeout: 31000,
        validateStatus: (s) => s === 200 || s === 304 || s === 204,
      });

      // --- 공통: ETag 반영 + 부팅 epoch 변경 감지 ---
      const rawEtag = res.headers?.etag || "";
      const normalized = rawEtag.replace(/"/g, "").replace(/^W\//, "");
      if (normalized) {
        const prevBoot = bootFromEtag(etag.value);
        const newBoot = bootFromEtag(normalized);

        // 서버 재기동 감지 시: 커서/ETag 초기화 후 즉시 재개 (재귀 호출 X)
        if (!epochRetry && prevBoot && newBoot && prevBoot !== newBoot) {
          resetCursor("0");
          failCount = 0;
          etag.value = "";
          localStorage.removeItem(etagKey);
          schedule(0);
          return;
        }

        // === ETag에서 id 추출 및 커서 동기화(축소 허용) ===
        const idEtStr = idFromEtag(normalized);
        const idEt = idEtStr == null ? null : Number(idEtStr);
        const last = Number(lastId.value);

        if (idEt != null && Number.isFinite(idEt)) {
          if (idEt < last || idEt > last) {
            lastId.value = String(idEt);
            localStorage.setItem(cursorKey, lastId.value);
          }
        }
        etag.value = normalized;
        localStorage.setItem(etagKey, normalized);
      }

      // --- 상태별 처리 ---
      if (res.status === 204) {
        // 타임아웃 등 변화 없음
        dbg("tick response 204 (no content)");
        failCount = 0;
        schedule(0); // 즉시 다음 long-poll
        return;
      }
      if (res.status === 304) {
        // ETag 동일
        dbg("tick response 304 (not modified)");
        failCount = 0;
        schedule(0);
        return;
      }
      // 200
      dbg("tick response 200", res.data);
      const { items, truncated, nextAfter, rejected } = unwrapEnvelope(
        res.data,
      );
      if (!rejected) {
        pushItems(items);
        if (Number(nextAfter) > Number(lastId.value)) {
          lastId.value = String(nextAfter);
          localStorage.setItem(cursorKey, lastId.value);
        }
      }
      failCount = 0;
      // 더 남아있으면 즉시 한 번 더, 아니면 바로 다음 long-poll
      schedule(0);
      return;
    } catch (err) {
      dbg("tick error", err?.response?.status, err?.message);
      // 실패: 백오프 계산
      failCount++;
      let delay = calcBackoffMs();

      // 서버가 Retry-After를 주면 존중
      const raMs = parseRetryAfterMs(err?.response);
      if (raMs != null) delay = Math.max(delay, raMs);

      // 네트워크 완전 끊김이면 온라인 때 재개
      if (navigator?.onLine === false) {
        stopPolling();
        window.addEventListener("online", () => startPolling(), { once: true });
        return;
      }

      schedule(delay);
    } finally {
      inFlight = false;
    }
  }

  function startPolling() {
    if (running.value) return; // 중복 시작 방지
    running.value = true;
    failCount = 0;
    const suid = readUserSuid();
    dbg("startPolling", { suid });
    if (suid) {
      loadServerUnread(); // 서버 영속 알림 수를 뱃지에 반영(새로고침 대응)
      schedule(0); // 즉시 1회 시작(이후 long-poll이 대기)
    }
  }

  function stopPolling() {
    running.value = false;
    clearTimeout(timer.value);
    timer.value = null;
    inFlight = false;
  }

  function resetCursor(id = "0") {
    lastId.value = id;
    localStorage.setItem(cursorKey, id);
    etag.value = "";
    localStorage.removeItem(etagKey);
  }

  function onLogin() {
    resetCursor("0");
    clearInbox();
    serverAlarms.value = [];
    startPolling();
    loadServerUnread(); // 이미 polling 중이어도 로그인 직후 서버 알림 재로드
  }
  function onLogout() {
    stopPolling();
    resetCursor("0");
    clearInbox();
    serverAlarms.value = [];
  }

  return {
    lastId,
    inbox,
    unreadCount,
    running,
    message,
    type,
    visible,
    showAlert,
    closeAlert,
    refreshNow,
    loadServerUnread,
    startPolling,
    stopPolling,
    resetCursor,
    markAllRead,
    markRead,
    clearInbox,
    removeInboxItem,
    pushInboxMessage,
    onLogin,
    onLogout,
    pushEstimateAlert,
  };
});
